/* eslint-disable max-lines-per-function */
import { Abschnitt } from '@/domain/mitarbeiter/features/rotationsplan/entities/Abschnitt';
import { Arbeitsbereich } from '@/domain/mitarbeiter/features/rotationsplan/entities/Arbeitsbereich';
import { Abteilungsleiter } from '@/domain/mitarbeiter/features/rotationsplan/entities/Abteilungsleiter';
import { Beruf } from '@/domain/mitarbeiter/features/rotationsplan/entities/Beruf';
import { Mitarbeiter } from '@/domain/mitarbeiter/features/rotationsplan/entities/Mitarbeiter';
import { Rotationsplan } from '@/domain/mitarbeiter/features/rotationsplan/entities/Rotationsplan';
import { Standort } from '@/domain/mitarbeiter/features/rotationsplan/entities/Standort';
import {
  AbwesenheitsStation,
  ArbeitsbereichStation,
} from '@/domain/mitarbeiter/features/rotationsplan/entities/Station';
import { ROTATIONSPLAENE_BY_STANDORT_IDS } from '@/domain/mitarbeiter/features/rotationsplan/hooks/useRotationsplaene/graphql/queries';
import {
  Mitarbeiter_RotationsplaeneByStandortIdsQuery,
  Mitarbeiter_RotationsplaeneByStandortIdsQueryVariables,
} from '@/gql/graphql';
import { useSuspenseQuery } from '@apollo/client/react';
import * as Sentry from '@sentry/react';
import { useMemo } from 'react';
import { Abwesenheit } from '@/domain/mitarbeiter/features/rotationsplan/entities/Abwesenheit';

export function useRotationsplaene(standortIds: string[]): {
  rotationsplaene: Rotationsplan[];
  error?: string;
} {
  const result = useSuspenseQuery<
    Mitarbeiter_RotationsplaeneByStandortIdsQuery,
    Mitarbeiter_RotationsplaeneByStandortIdsQueryVariables
  >(ROTATIONSPLAENE_BY_STANDORT_IDS, {
    variables: { standortIds },
  });

  const rotationsplaene = useMemo(
    () =>
      result.data?.mitarbeiter_rotationsplaeneByStandortIds.map((plan) => {
        const abschnitte = plan.abschnitte.map((abschnitt) => {
          const stationen = abschnitt.stationen.map((station) => {
            if (
              station.__typename ===
              'mitarbeiter_rotationsplanArbeitsbereichStation'
            ) {
              return new ArbeitsbereichStation({
                id: station.id,
                abschnittId: abschnitt.id,
                von: new Date(station.von),
                bis: new Date(station.bis),
                arbeitsbereich: new Arbeitsbereich({
                  id: station.arbeitsbereich.id,
                  markierung: station.arbeitsbereich.markierung,
                  name: station.arbeitsbereich.name,
                  standort: new Standort({
                    id: station.arbeitsbereich.standort.id,
                    name: station.arbeitsbereich.standort.name,
                    storenummer: station.arbeitsbereich.standort.storenummer,
                  }),
                }),
                lernziele: station.lernziele,
                abteilungsleiter: station.abteilungsleiter
                  ? new Abteilungsleiter({
                      id: station.abteilungsleiter.id,
                      vorname: station.abteilungsleiter.vorname,
                      nachname: station.abteilungsleiter.nachname,
                    })
                  : undefined,
              });
            } else {
              return new AbwesenheitsStation({
                id: station.id,
                abschnittId: abschnitt.id,
                von: new Date(station.von),
                bis: new Date(station.bis),
                titel: station.name,
              });
            }
          });

          return new Abschnitt({
            id: abschnitt.id,
            name: abschnitt.name,
            position: abschnitt.position,
            stationen,
          });
        });

        const abwesenheiten = plan.abwesenheiten
          ? plan.abwesenheiten.map(
              (a) =>
                new Abwesenheit({
                  id: a.id,
                  von: new Date(a.von),
                  bis: new Date(a.bis),
                  grund: a.grund,
                  faktor: a.faktor,
                }),
            )
          : [];

        return new Rotationsplan({
          id: plan.id,
          land: plan.land,
          standortId: plan.standortId,
          mitarbeiter: new Mitarbeiter({
            id: plan.mitarbeiter.id,
            vorname: plan.mitarbeiter.vorname,
            nachname: plan.mitarbeiter.nachname,
            imgUrl: plan.mitarbeiter.bildUrl ?? undefined,
            arbeitsbereich: plan.mitarbeiter.arbeitsbereich
              ? new Arbeitsbereich({
                  id: plan.mitarbeiter.arbeitsbereich.id,
                  markierung: plan.mitarbeiter.arbeitsbereich.markierung,
                  name: plan.mitarbeiter.arbeitsbereich.name,
                  standort: new Standort({
                    id: plan.mitarbeiter.arbeitsbereich.standort.id,
                    name: plan.mitarbeiter.arbeitsbereich.standort.name,
                    storenummer:
                      plan.mitarbeiter.arbeitsbereich.standort.storenummer,
                  }),
                })
              : undefined,
            standort: new Standort({
              id: plan.mitarbeiter.standort.id,
              name: plan.mitarbeiter.standort.name,
              storenummer: plan.mitarbeiter.standort.storenummer,
            }),
          }),
          beruf: new Beruf({
            id: plan.beruf.id,
            name: plan.beruf.name,
            markierung: plan.beruf.markierung,
            abkuerzung: plan.beruf.abkuerzung ?? '',
            istAusbildung: plan.beruf.istAusbildung,
          }),
          abschnitte,
          abwesenheiten,
        });
      }) ?? [],
    [result.data],
  );

  if (result.error) {
    Sentry.captureException(result.error);
    return { rotationsplaene: [] };
  }

  return { rotationsplaene };
}
